import { spacings } from "@/assets/themes";
import { CardBase } from "@/components/Styles/Base";
import polyglot from "@/utils/polyglot";
import { useFormikContext } from "formik";
import moment from "moment";
import { useEffect } from "react";
import { getTranslatedDay } from "@/utils";
import { Body16, List } from "../../components";
import Radio from "../../components/Radio";
import StepTitle from "../categoryForms/common/StepTitle";
import {
  FormSelectables,
  StyledFormControl,
} from "../categoryForms/common/fields";

const EditFrequencySelection = ({ title }) => {
  const { values, setFieldValue } = useFormikContext();

  const handleSelection = (frequency) => {
    setFieldValue("frequency", frequency);
  };

  const POSITION_OPTIONS = Array.from({ length: 4 }, (_, i) => ({
    label: [
      polyglot.t("frequency.first"),
      polyglot.t("frequency.second"),
      polyglot.t("frequency.third"),
      polyglot.t("frequency.last"),
    ][i],
    translation: ["first", "second", "third", "last"][i],
    value: i + 1,
  }));

  const DAYS = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const setInitialValuesFromStartDate = () => {
    if (values.start_date && !values.wday) {
      const startDate = moment(values.start_date);
      setFieldValue("wday", DAYS[startDate.isoWeekday() - 1]);
      if (values.frequency === "monthly_by_weekday") {
        const getPosition = (date) => {
          const momentDate = moment(date);
          const weekInMonth = Math.ceil(momentDate.date() / 7);
          const lastDayOfMonth = momentDate.clone().endOf("month");
          const daysInMonth = lastDayOfMonth.date();
          const remainingDays = daysInMonth - momentDate.date();

          if (remainingDays < 7) {
            return POSITION_OPTIONS.length - 1;
          }
          return weekInMonth;
        };
        const occurrence = getPosition(values.start_date) || undefined;
        if (occurrence) {
          setFieldValue("wposition", occurrence);
        }
      }
    }
  };

  useEffect(() => {
    setInitialValuesFromStartDate();
  }, [values.start_date]);

  useEffect(() => {
    if (values.frequency !== "monthly_by_weekday") {
      setFieldValue("wposition", undefined);
    }
  }, [values.frequency]);

  const OPTIONS = [
    {
      id: "weekly",
      title: polyglot.t("frequency.weekly"),
    },
    {
      id: "biweekly",
      title: polyglot.t("frequency.biweekly"),
    },
    {
      id: "monthly_by_weekday",
      title: polyglot.t("frequency.monthly"),
    },
  ];

  const DAY_OPTIONS = Array.from({ length: 7 }, (_, i) => {
    const day = moment().startOf("week").add(i, "days");
    return {
      label: day.format("dd"),
      value: DAYS[i],
      translated: getTranslatedDay(DAYS[i]),
    };
  });

  const getSelectedDayName = () =>
    DAY_OPTIONS.find((day) => day.value === values.wday)?.translated;

  return (
    <>
      <StepTitle>{title}</StepTitle>
      <StyledFormControl
        label={polyglot.t("recurring.select_a_frequency")}
        name="frequency"
      >
        <CardBase
          border
          flat
          css={`
            padding-top: ${spacings.xs};
            padding-bottom: ${spacings.xs};
          `}
        >
          <List.Group>
            {OPTIONS.map((option) => (
              <List.Item
                key={option.id}
                withGutters
                onClick={() => handleSelection(option.id)}
                RightComponent={() => (
                  <Radio checked={values.frequency === option.id} />
                )}
              >
                <Body16>{option.title}</Body16>
              </List.Item>
            ))}
          </List.Group>
        </CardBase>
      </StyledFormControl>
      <StyledFormControl name="wday" label={polyglot.t("frequency.select_day")}>
        <FormSelectables options={DAY_OPTIONS} name="wday" />
      </StyledFormControl>
      {values.frequency === "monthly_by_weekday" && (
        <StyledFormControl
          name="wposition"
          label={polyglot.t("frequency.which_day_of_month", {
            day: getSelectedDayName().toLowerCase(),
          })}
          hint={
            values.wposition &&
            polyglot.t(
              `frequency.occurrence.${
                POSITION_OPTIONS.find(
                  (option) => option.value === values.wposition
                ).translation
              }`,
              {
                day: getSelectedDayName().toLowerCase(),
              }
            )
          }
        >
          <FormSelectables name="wposition" options={POSITION_OPTIONS} isWrap />
        </StyledFormControl>
      )}
    </>
  );
};

export default EditFrequencySelection;
